import React, { useEffect, useMemo, useState } from "react";

import AwesomeDebouncePromise from "awesome-debounce-promise";

import { Card, Form, Switch, InputNumber, Input, notification } from "antd";

import OptionService from "@Services/OptionService";
import { IOptionDefinitionDetails, IOptionInstanceDetails } from "@Models/IOptionDefinitionDetails";
import { SkeletonRows } from "@Components/shared/SkeletonRows";
import { getSystemId } from "@Utils";
import MiniSpinner from "@Components/shared/MiniSpinner";

import "@Styles/components/accountOptionsForm.scss";

const AccountSystemTab = () => {
    const [systemDefinitions, setSystemDefinitions] = useState<IOptionDefinitionDetails[]>(null);
    const [systemInstances, setSystemInstances] = useState<IOptionInstanceDetails[]>([]);
    const [isFetching, setFetching] = useState(false);
    const [isUpdating, setUpdating] = useState(false);

    /**
     * Update a system option.
     * @param optionId
     * @param value
     */
    const updateSystemOption = async (optionId: string, value: string) => {
        setUpdating(true);
        const service = new OptionService();
        const newOption: IOptionInstanceDetails = {
            instanceId: getSystemId(),
            optionId: optionId,
            value: value,
        };
        await service.updateSystem(newOption);

        setSystemInstances((prevInstances) => {
            const newInstances = [...prevInstances];
            const index = newInstances.findIndex((x) => x.optionId === optionId);
            if (index !== -1) {
                newInstances[index] = newOption;
                return newInstances;
            }
            return [...prevInstances, newOption];
        });

        setUpdating(false);
        notification.success({ message: "System option updated." });
    };

    /**
     * Debounced version of the update function
     */
    const debouncedUpdateSystemOption = useMemo(
        () => AwesomeDebouncePromise(updateSystemOption, 2000, { onlyResolvesLast: false }),
        // We don't need to include the function in dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    /**
     * Fetch the system option definitions and the instances.
     */
    const fetchSystemOptions = async () => {
        setFetching(true);
        const service = new OptionService();
        const definitionsResult = await service.getSystemDefinitions();
        if (!definitionsResult.hasErrors) {
            setSystemDefinitions(definitionsResult.value);
            const instanceResult = await service.getSystem();
            if (!instanceResult.hasErrors) {
                setSystemInstances(instanceResult.value);
            }
        }
        setFetching(false);
    };

    useEffect(() => {
        fetchSystemOptions().catch(() => {});
    }, []);

    /**
     * Render a user option, falling back to the definition default where required.
     * @param option
     */
    const renderOption = (option: IOptionDefinitionDetails) => {
        let value = "";
        const index = systemInstances.findIndex((x) => x.optionId === option.optionId);
        value = index === -1 ? option.defaultValue : systemInstances[index].value;

        switch (option.type.toLowerCase()) {
            case "bool":
                return (
                    <Switch
                        checkedChildren="Yes"
                        unCheckedChildren="No"
                        defaultChecked={value === "true"}
                        onChange={(checked) => debouncedUpdateSystemOption(option.optionId, checked ? "true" : "false")}
                    />
                );

            case "int":
                return (
                    <InputNumber
                        defaultValue={value}
                        onChange={(value) => debouncedUpdateSystemOption(option.optionId, value.toString())}
                    />
                );

            case "string":
                return (
                    <Input
                        defaultValue={value}
                        onChange={(e) => debouncedUpdateSystemOption(option.optionId, e.target.value)}
                    />
                );

            case "user":
                break;
            case "userlist":
                break;
            case "boollist":
                break;
            case "intlist":
                break;
            case "stringlist":
                break;
        }
        return null;
    };

    return (
        <Card
            title={
                <>
                    System Options &nbsp;&nbsp;
                    {isUpdating && (
                        <span
                            key={"spin-item-searching"}
                            style={{ display: "inline-block", justifyContent: "left", paddingLeft: 10 }}
                        >
                            <MiniSpinner />
                        </span>
                    )}
                </>
            }
            className="newUI-raised"
        >
            {isFetching ? (
                <SkeletonRows />
            ) : (
                <Form className={"account-options-form"}>
                    <table>
                        <tbody>
                            {systemDefinitions?.map((x, index) => (
                                <tr key={index}>
                                    <td>{x.label}</td>
                                    <td>
                                        <Form.Item>{renderOption(x)}</Form.Item>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Form>
            )}
        </Card>
    );
};

export default AccountSystemTab;
